import React, {useContext, useState, useMemo, useRef, useCallback, useEffect} from 'react'
import _ from 'lodash'
import AllDayEvents from './AllDayEvents'
import DateTimeIndicator from './DateTimeIndicator'
import DayHead from './DayHead'

import moment from 'moment'
import DayColumn from './DayColumn'
import TimeColumn from './TimeColumn'
import ApMath from '../../utils/ApMath'
import {snap} from '../../utils/ApMath'
import useAllDayReserveAction from './useAllDayReserveAction'
import WalkingPanda from '../../common/LoadingIndicators/WalkingPanda'
import useInitLoader from './useInitLoader'

const Week = ({
  onClickEvent = () => {},
  onNavigate = () => {},
  showSidebar,
  events,
  selectedDate,
  willLoad = true,
}) => {
  const refMain = useRef(null)

  const [showLoader] = useInitLoader(events.isLoading)

  const [dayEvents, allDayEvents] = useMemo(() => {
    const hoursInMS = 60 * 60 * 1000
    const dayInMS = hoursInMS * 24

    let selDateStart = selectedDate.clone().startOf('week')
    let selDateEnd = selectedDate.clone().endOf('week')

    let allDayEvents = {}
    let dayEvents = {}
    Array.from({length: 7}).forEach((e, i) => {
      const index = selDateStart
        .clone()
        .add(i, 'days')
        .valueOf()
      allDayEvents[index] = []
      dayEvents[index] = []
    })

    events.data.forEach(ev => {
      const start_date = moment(ev.start)
      let real_end = moment(ev.end)
      let end_date = moment(ev.action_return_date || ev.end)

      // fix if fulfilled overdue
      if (
        ev.type === 'SystemReservationAction' &&
        ev.state === 'overdue' &&
        ev.status === 'fulfilled'
      ) {
        end_date = moment()
      }

      if (
        !ApMath.isRangeOverlap(
          selDateStart.valueOf(),
          selDateEnd.valueOf(),
          start_date
            .clone()
            .startOf('day')
            .valueOf(),
          end_date
            .clone()
            .startOf('day')
            .valueOf()
        )
      ) {
        return
      }

      let eventDateRef = moment(Math.max(start_date.valueOf(), selDateStart.valueOf()))
        .startOf('day')
        .valueOf()

      let top = ((start_date.valueOf() - eventDateRef) / dayInMS) * 100
      let bottom = ((end_date.valueOf() - eventDateRef) / dayInMS) * 100

      // fix titles.
      let title = ''
      if (ev.display_name_first || ev.display_with_secondary || ev.display_name_third) {
        if (ev.type === 'SystemReservationAction') {
          title = `${ev.display_name_first}, ${ev.display_with_secondary}, ${ev.display_name_third}`
        } else if (ev.type === 'ReservationAction') {
          title = `${ev.display_name_first}, ${ev.display_with_secondary}, ${ev.display_name_third}`
        } else {
          title = `${ev.display_name_first}, ${ev.display_with_secondary}, ${ev.display_name_third}`
          let _startSnap = snap(
            start_date.valueOf() - selectedDate.valueOf(),
            hoursInMS * 1,
            Math.floor
          )
          top = (_startSnap / dayInMS) * 100
          bottom = ((_startSnap + hoursInMS * 1) / dayInMS) * 100
        }
      } else {
        if (ev.type === 'SystemReservationAction') {
          title = `#${ev?.reservation_uid || ev?.entity_display_name}, ${
            ev.reserved_for
          }, ${start_date.format('MMMM Do')} - ${real_end.format('MMMM Do')}, Items (${ev.items})`
        } else if (ev.type === 'ReservationAction') {
          title = `${ev.entity_display_name}, ${ev.display_name}, ${start_date.format(
            'MMMM Do'
          )} - ${real_end.format('MMMM Do')}`
        } else if (ev.type === 'DefaultAction') {
          title = `${ev.entity_display_name}, ${ev.display_name}, ${start_date.format('MMMM Do')}`
          // snap to floor ms
          let _startSnap = snap(
            start_date.valueOf() -
              start_date
                .clone()
                .startOf('day')
                .valueOf(),
            hoursInMS * 0.5,
            Math.floor
          )
          top = (_startSnap / dayInMS) * 100
          bottom = ((_startSnap + hoursInMS * 0.5) / dayInMS) * 100
        } else {
          title = `${ev.entity_display_name || ev.display_name}, ${
            ev.field_name
          }, ${start_date.format('MMMM Do')}`

          // snap to floor ms
          let _startSnap = ApMath.snap(
            start_date.valueOf() -
              start_date
                .clone()
                .startOf('day')
                .valueOf(),
            hoursInMS * 0.5,
            Math.floor
          )
          top = (_startSnap / dayInMS) * 100
          bottom = ((_startSnap + hoursInMS * 0.5) / dayInMS) * 100
        }
      }
      if (top < 0 || bottom >= 100) {
        if (!allDayEvents[eventDateRef]) {
          dayEvents[eventDateRef] = []
        }
        // all day
        allDayEvents[eventDateRef].push({
          event: ev,
          top: top,
          bottom: bottom,
          title,
        })
      } else {
        if (!dayEvents[eventDateRef]) {
          dayEvents[eventDateRef] = []
        }

        dayEvents[eventDateRef].push({
          event: ev,
          top: top,
          bottom: bottom,
          left: 0,
          right: 0,
          title,
        })
      }
    })

    allDayEvents = _.map(allDayEvents, events =>
      events.sort((a, b) => {
        if (a.bottom - a.top > b.bottom - b.top) return -1
        else if (a.bottom - a.top === b.bottom - b.top && a.top < b.top) return -1
        else return 1
      })
    )

    dayEvents = _.map(dayEvents, events => events)

    return [dayEvents, allDayEvents]
  }, [events.data, selectedDate])

  const daysOfWeek = useMemo(() => {
    let startOfWeek = selectedDate.clone().startOf('week')

    return Array.from({length: 7}).map((e, i) => {
      return startOfWeek.clone().add(i, 'days')
    })
  }, [selectedDate])

  const now = moment()

  // scrolling
  useEffect(() => {
    if (!refMain.current) return
    let _now = now.clone().startOf('week')
    let _selDate = selectedDate.clone().startOf('week')
    if (_selDate.valueOf() === _now.valueOf()) {
      // trigger scrolling
      const rect = refMain.current.getBoundingClientRect()
      const hours =
        (now.valueOf() -
          now
            .clone()
            .startOf('day')
            .valueOf()) /
        (60 * 60 * 1000)
      refMain.current.scrollTop = hours * 64 - rect.height * 0.5
    }
  }, [now, selectedDate])

  useAllDayReserveAction('.c-calendar-day [data-x="all-day-range"]', daysOfWeek[0], 7)

  return (
    <div className="c-calendar-day ap-no-select">
      <div className="header">
        <div className="time-column"></div>
        <div className="day-columns" data-x="all-day-range">
          {daysOfWeek.map((e, i) => (
            <div key={i} className="day-column" data-x="all-day-range">
              <DayHead
                date={e}
                now={now}
                style={{margin: 'auto'}}
                onClick={ev => onNavigate(ev, e, events)}
              />
            </div>
          ))}
        </div>
      </div>
      {<WalkingPanda hidden={!showLoader || !willLoad} style={{top: 70, paddingTop: 120}} />}
      <AllDayEvents
        data={allDayEvents}
        selectedDate={daysOfWeek[0]}
        onClickEvent={onClickEvent}
        dayCount={7}
      />
      <div className="main ap-scrollbar" ref={refMain}>
        <div className="main-wrapper">
          <DateTimeIndicator now={now} selectedDate={daysOfWeek[0]} dayCount={7} />
          <TimeColumn key={`tc_i_${showSidebar}`} />
          {dayEvents.map((_events, i) => (
            <DayColumn
              key={`dc_${i}_${showSidebar}`}
              date={daysOfWeek[i]}
              events={_events}
              onClickEvent={onClickEvent}
              onClickMoreSidebar={e => onNavigate(e, daysOfWeek[i], events)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default Week
